import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AdminPromoOfferDialog } from '@/components/AdminPromoOfferDialog';

const isLive = (o) => {
  const now = new Date().toISOString();
  return o.status === 'active' && (!o.starts_at || o.starts_at <= now) && (!o.ends_at || o.ends_at > now);
};

export const AdminPromoOffers = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/promo-offers').then(({ data }) => setItems(data.items || [])).catch(() => toast.error('No se pudieron cargar las promociones'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (o) => {
    if (!window.confirm(`¿Eliminar la promoción «${o.title}»?`)) return;
    try { await api.delete(`/admin/promo-offers/${o.offer_id}`); toast.success('Promoción eliminada'); load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  return (
    <section className="mt-12 border-t border-slate-200 pt-8" data-testid="admin-promo-offers">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-black tracking-tighter text-slate-900">Promociones independientes</h2>
          <p className="mt-1 text-sm text-slate-500">Publicaciones informativas (sin carrito ni pago) que se muestran en Inicio y en /promociones.</p>
        </div>
        <Button className="rounded-none bg-slate-900 hover:bg-orange-600" onClick={() => setEditing({})} data-testid="admin-promo-offer-new-btn">
          <Plus className="mr-1 h-4 w-4" /> Nueva promoción
        </Button>
      </div>
      <div className="mt-4 divide-y divide-slate-200 border border-slate-200 bg-white">
        {items.length === 0 && <div className="p-6 text-sm text-slate-500" data-testid="admin-promo-offers-empty">Aún no hay promociones independientes.</div>}
        {items.map((o) => (
          <div key={o.offer_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`admin-promo-offer-row-${o.offer_id}`}>
            <div>
              <div className="font-bold text-slate-900">{o.title}</div>
              <div className="text-xs text-slate-500">
                {o.price != null ? formatCOP(o.price) : 'Sin precio'}{o.promo_label ? ` · ${o.promo_label}` : ''}
                {' · '}{o.starts_at ? formatDate(o.starts_at) : 'Desde ya'} → {o.ends_at ? formatDate(o.ends_at) : 'Sin fin'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={isLive(o) ? 'bg-emerald-600' : 'bg-slate-400'} data-testid={`admin-promo-offer-status-${o.offer_id}`}>
                {isLive(o) ? 'Publicada' : o.status === 'inactive' ? 'Inactiva' : 'Fuera de vigencia'}
              </Badge>
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(o)} data-testid={`admin-promo-offer-edit-${o.offer_id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="rounded-none text-red-600" onClick={() => remove(o)} data-testid={`admin-promo-offer-delete-${o.offer_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <AdminPromoOfferDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </section>
  );
};
